import {
  parseDocument as parseAfpDocument,
  type AfpDocument,
  type AfpMedia,
  type AfpMediaRendition,
  type Shot
} from 'afpnews-api'
import { normalizeEventId } from '@/lib/events'

export enum Urgency {
  Flash = 1,
  Alert,
  Urgent,
  Normal
}

export type MediaSize = AfpMediaRendition

export type PictureMedia = {
  id: string
  creator?: string
  provider?: string
  caption?: string
  dateline: string
  pictureSizes: MediaSize[]
}

export type VideoMedia = {
  id: string
  creator?: string
  provider?: string
  dateline: string
  videoSizes: MediaSize[]
  pictureSizes: MediaSize[]
}

export type MultiMedia = {
  id: string
  creator?: string
  provider?: string
  caption?: string
  dateline: string
  sizes: MediaSize[]
}

type GenericFields = {
  id: string
  shortId?: string
  source?: string
  headline?: string
  created: Date
  published: Date
  embargoed?: Date
  lang: string
  events: { id: string; name: string }[]
  slugs?: string[]
  keywords?: string[]
  country: { id?: string; name?: string }
  city?: string
  location?: { lat: number; lon: number }
  revision: number
  disclaimer?: string[]
  provider: string
  creator?: string
  status: AfpDocument['status']
  signal?: AfpDocument['signal']
  advisory?: string
}

export type TextDocument = GenericFields & {
  type: 'text' | 'factcheck'
  paragraphs: string[]
  genre?: string
  urgency: number
  hasBeenAlerted: boolean
}

export type PictureDocument = GenericFields & {
  type: 'photo' | 'graphic'
  media: PictureMedia
  topshot: boolean
}

export type VideoDocument = GenericFields & {
  type: 'video'
  caption: string
  media: VideoMedia
  shots: Shot[]
}

export type MultimediaDocument = GenericFields & {
  type: 'multimedia'
  paragraphs: string[]
  genre?: string
  urgency: number
  hasBeenAlerted: boolean
  medias: MultiMedia[]
  topic?: string[]
}

export type WebStoryDocument = GenericFields & {
  type: 'webstory'
  medias: MultiMedia[]
  href?: string
}

export type Document =
  | TextDocument
  | PictureDocument
  | VideoDocument
  | MultimediaDocument
  | WebStoryDocument

// Rôles reconnus pour une image affichable en pleine taille (hors bandeau AFP `Preview_W`,
// géré séparément côté viewer) : exclut les vignettes spécifiques (ex. Quicklook, Squared120)
// dont le ratio ne correspond pas à la photo — ProgressiveImage.vue prend `pictureSizes[0]`
// par position pour calculer le ratio d'affichage, donc ce filtre est structurel, pas cosmétique.
const PICTURE_ROLES = new Set([
  'HighDef',
  'Preview',
  'Preview_B',
  'Preview_W',
  'Thumbnail'
])

function toPictureSizes(renditions: AfpMediaRendition[]): MediaSize[] {
  return renditions.filter(r => r.type === 'Photo' && PICTURE_ROLES.has(r.role))
}

function toPictureMedia(media: AfpMedia): PictureMedia {
  return {
    id: media.uno,
    creator: media.creator,
    provider: media.provider,
    caption: media.caption,
    dateline: media.dateline,
    pictureSizes: toPictureSizes(media.renditions).toSorted(
      (a, b) => a.width - b.width
    )
  }
}

function toVideoMedia(media: AfpMedia): VideoMedia {
  return {
    id: media.uno,
    creator: media.creator,
    provider: media.provider,
    dateline: media.dateline,
    // .mpg (Mpeg2) renditions ne sont pas lisibles nativement par les navigateurs
    videoSizes: media.renditions
      .filter(r => r.type === 'Video' && r.href.endsWith('.mp4'))
      .toSorted((a, b) => b.width - a.width),
    pictureSizes: toPictureSizes(media.renditions).toSorted(
      (a, b) => b.width - a.width
    )
  }
}

function toMultiMedia(media: AfpMedia): MultiMedia {
  const sizes = toPictureSizes(media.renditions).concat(
    media.renditions.filter(r => r.type === 'Video' && r.href.endsWith('.mp4'))
  )
  return {
    id: media.uno,
    creator: media.creator,
    provider: media.provider,
    caption: media.caption,
    dateline: media.dateline,
    sizes: sizes.toSorted((a, b) => a.width - b.width)
  }
}

function toBase(doc: AfpDocument): GenericFields {
  return {
    id: doc.uno,
    shortId: doc.shortId,
    source: doc.source,
    headline: doc.headline,
    created: doc.created,
    published: doc.published,
    embargoed: doc.embargoed,
    lang: doc.lang,
    // Les ids d'événement AFP ne sont pas garantis casse-stable d'une dépêche
    // à l'autre côté API — normalisé ici, au point d'entrée unique de tout
    // consommateur de Document.events (store local, dont la recherche
    // d'événements locale du tool IA search_events).
    events: doc.events.map(ev => ({ ...ev, id: normalizeEventId(ev.id) })),
    slugs: doc.slugs,
    keywords: doc.keywords,
    country: doc.country,
    city: doc.city,
    location: doc.location,
    revision: doc.revision,
    disclaimer: doc.disclaimer,
    provider: doc.provider,
    creator: doc.creator,
    status: doc.status,
    signal: doc.signal,
    advisory: doc.advisory
  }
}

export function toDocument(afpDoc: AfpDocument): Document {
  const base = toBase(afpDoc)

  // afpDoc.class (afpnews-api, 8 valeurs dont 'picture'/'videography') est ici
  // volontairement réduit au vocabulaire DocumentType du deck (7 valeurs) :
  // 'picture' -> 'photo', 'video'/'videography' -> 'video'. Un nouvel ajout
  // côté API n'est PAS répercuté automatiquement ici.
  switch (afpDoc.class) {
    case 'text':
    case 'factcheck':
      return {
        ...base,
        type: afpDoc.class,
        paragraphs: afpDoc.paragraphs.map(p => p.text),
        genre: afpDoc.genre,
        urgency: afpDoc.urgency,
        hasBeenAlerted: afpDoc.hasBeenAlerted
      }

    case 'multimedia':
      return {
        ...base,
        type: 'multimedia',
        paragraphs: afpDoc.paragraphs.map(p => p.text),
        genre: afpDoc.genre,
        urgency: afpDoc.urgency,
        hasBeenAlerted: afpDoc.hasBeenAlerted,
        medias: afpDoc.medias.map(toMultiMedia),
        topic: afpDoc.topics
      }

    case 'picture':
    case 'graphic': {
      const firstMedia = afpDoc.medias[0]
      if (!firstMedia) throw new Error('No media found in picture document')
      const media = toPictureMedia(firstMedia)
      if (media.pictureSizes.length === 0) {
        throw new Error('No media found in picture document')
      }
      return {
        ...base,
        type: afpDoc.class === 'picture' ? 'photo' : 'graphic',
        media,
        topshot: afpDoc.topshot
      }
    }

    case 'video':
    case 'videography': {
      const firstMedia = afpDoc.medias[0]
      if (!firstMedia) throw new Error('No media found in video document')
      const media = toVideoMedia(firstMedia)
      if (media.videoSizes.length === 0 && media.pictureSizes.length === 0) {
        throw new Error('No media found in video document')
      }
      return {
        ...base,
        type: 'video',
        caption: afpDoc.caption,
        media,
        shots: afpDoc.shots
      }
    }

    case 'webstory':
      return {
        ...base,
        type: 'webstory',
        medias: afpDoc.medias.map(toMultiMedia),
        href: afpDoc.href
      }
  }
}

export default function parseDocument(
  docSource: unknown
): Document | undefined {
  try {
    return toDocument(parseAfpDocument(docSource))
  } catch (e) {
    console.debug(`Erreur lors de la conversion du document`, docSource)
    console.warn(e)
  }
  return undefined
}
